import {ApiResponseDs, SaveOrRollback, ThemeMixin, useIsMounted} from "../../../../@bizkeytech";
import {Box, CircularProgress, IconButton, inputBaseClasses, Theme, Tooltip, Typography} from "@mui/material";
import React, {Dispatch, ReactNode, SetStateAction, useCallback, useLayoutEffect, useMemo, useState} from "react";
import Utils from "../../../../core/services/utils";
import {HistoryRounded} from "@mui/icons-material";
import UIUtils from "../../../utils";
import {SxProps} from "@mui/system";
import {flushSync} from "react-dom";

export const RevertValuesSaveFieldResponse = "Revert";

export type SaveFieldResponse = Pick<ApiResponseDs, 'resultFlag' | 'message'>;
export type SaveFieldCallback<
    Value
> = (value?: Value | undefined, ...args: any[]) => Promise<SaveFieldResponse | undefined | void | typeof RevertValuesSaveFieldResponse>;

export type FieldControlProps<
    Value,
    Readonly extends boolean | undefined,
    FieldProps extends object | undefined,
> = {
    label?: ReactNode;
    labelSx?: SxProps<Theme>,
    value?: Value | undefined;
    required?: boolean;
    disabled?: boolean;
    readonly?: Readonly;
    onSave?: Readonly extends false ? SaveFieldCallback<Value> : never;
    onHistoryClicked?: Readonly extends false ? VoidFunction : never;
    Field: React.ComponentType<FieldProps>;
    helperText?: string,
    map?: boolean;
} & (FieldProps extends object ? { [key in keyof FieldProps]: FieldProps[key] } : {})

export type FieldValidation<Value = any> = (value: Value) => string | undefined | Promise<string | undefined>;

export interface FieldControlContextValue<Value = any> {
    addValidations: (...validations: FieldValidation<Value>[]) => void,
    clearValidations: () => void,
    value: Value | undefined,
    setValue: Dispatch<SetStateAction<Value | undefined>>,
    setValueFlushSync: Dispatch<SetStateAction<Value | undefined>>,
    saveChanges: () => Promise<void>,
    initialValue?: Value | undefined,
    savingChanges?: boolean,
    readonly?: boolean,
    disabled?: boolean,
    helperText?: string,
    erroneous?: boolean,
    sx?: ThemeMixin<Theme>,
}

const FieldControlContext = React.createContext<FieldControlContextValue>({
    setValue: () => void 0,
    setValueFlushSync: () => void 0,
    saveChanges: async () => void 0,
    addValidations: () => void 0,
    clearValidations: () => void 0,
    value: undefined,
    initialValue: undefined,
});


/**
 * Provides the field control context's value.
 */
export const useFieldControl = <T, >() => React.useContext<FieldControlContextValue<T>>(FieldControlContext);


const FieldControl = <
    Value = any,
    Readonly extends boolean = false,
    FieldProps extends object = {},
>(props: FieldControlProps<Value, Readonly, FieldProps>) => {
    const {
        label,
        value: valueProps,
        required,
        disabled,
        readonly,
        onSave,
        helperText,
        onHistoryClicked,
        ..._fieldProps
    } = props;
    const fieldProps = _fieldProps as FieldProps;

    const [value, _setValue] = useState<Value | undefined>(props.value);
    const valueRef = React.useRef<Value | undefined>(value);

    /**
     * Sets the value.
     */
    const setValue = useCallback<Dispatch<SetStateAction<Value | undefined>>>((val) => {
        _setValue(prevState => {
            const newState = typeof val === 'function' ? (val as Function)(prevState) : val;
            valueRef.current = newState;
            return newState;
        });
    }, [])

    const [savingChanges, setSavingChanges] = useState(false);
    const [validations, setValidations] = useState<Array<FieldValidation>>([]);
    const [response, setResponse] = useState<SaveFieldResponse>();
    const isMounted = useIsMounted();

    const valueChanged = useMemo(() => !Utils.deepEqual(value, props.value), [props.value, value]);
    const erroneous = useMemo(() => response?.resultFlag === false, [response]);

    /**
     * With each change in the [props.value] prop:
     * - updates the value.
     */
    useLayoutEffect(() => {
        setValue(props.value);
    }, [props.value, setValue]);

    /**
     * With each change in the [value] state:
     * - resets the response.
     */
    useLayoutEffect(() => {
        setResponse(undefined);
    }, [value]);

    /**
     * Rollbacks the changes.
     */
    const rollbackChanges = useCallback(() => setValue(props.value), [props.value, setValue]);

    /**
     * Saves the changes.
     */
    const saveChanges = useCallback(async () => {
        if (!isMounted())
            return;

        const value = valueRef.current;

        // Don't save if the value hasn't changed from initial value
        if (Utils.deepEqual(value, props.value)) {
            return;
        }

        const _validations = [...validations];
        if (props.required) {
            _validations.unshift((value) => !value ? "This field is required." : undefined);
        }
        for (const validation of _validations) {
            const errorMessage = await validation(value);
            if (errorMessage) {
                return setResponse({
                    resultFlag: false,
                    message: errorMessage
                });
            }
        }
        setSavingChanges(true);
        const response = await onSave?.(value);
        if (!isMounted())
            return;
        if (response === RevertValuesSaveFieldResponse) {
            return rollbackChanges();
        }
        setSavingChanges(false);
        if (!response?.resultFlag) {
            setResponse(!response ? undefined : {resultFlag: false, message: response.message ?? "An error occurred while saving the changes."});
        }
    }, [validations, props.required, onSave, isMounted, rollbackChanges, props.value]);

    /**
     * Creates the styles for the container boxes.
     *
     * @template {Theme}                        the theme template
     * @param {Theme} theme                     The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>} The styles for the container boxes
     */
    const fieldMixin = useCallback<ThemeMixin<Theme>>((theme) => ({
        ...((props.readonly || props.map) && {
            backgroundColor: 'background.tertiary',
            borderRadius: theme.vars?.shape?.borderRadius,
            'fieldset': {
                borderColor: 'background.tertiary',
            },
            [`.${inputBaseClasses.root}.${inputBaseClasses.disabled}, .${inputBaseClasses.root}:hover, .${inputBaseClasses.focused}`]: {
                'input': {
                    WebkitTextFillColor: theme.palette.text.primary,
                },
                'fieldset': {
                    borderColor: 'background.tertiary',
                },
            },

        }),
    }), [props.readonly, props.map])

    const addValidation = useCallback<FieldControlContextValue['addValidations']>((...validations) => setValidations(prevState => [...prevState, ...validations]), [])
    const clearValidation = useCallback<FieldControlContextValue['clearValidations']>(() => setValidations([]), []);

    /**
     * Sets the value with flush sync capability.
     */
    const setValueFlushSync = useCallback<Dispatch<SetStateAction<Value | undefined>>>((val) => flushSync(() => setValue(val)), [setValue]);

    const fieldControlContextValue = useMemo<FieldControlContextValue>(() => ({
        value,
        initialValue: props.value,
        setValue,
        setValueFlushSync,
        saveChanges,
        savingChanges,
        readonly: props.readonly,
        disabled: props.disabled || savingChanges || props.readonly,
        helperText: response?.message ?? helperText,
        erroneous,
        addValidations: addValidation,
        clearValidations: clearValidation,
        sx: fieldMixin,
    }), [value, props.value, props.readonly, props.disabled, setValue, setValueFlushSync, saveChanges, savingChanges, response?.message, helperText, erroneous, addValidation, clearValidation, fieldMixin]);

    return (
        <>
            <FieldControlContext.Provider value={fieldControlContextValue}>
                <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minHeight: 34}}>
                        <Typography
                            variant={'body2'}
                            fontWeight={600}
                            sx={(theme) => ({
                                ...UIUtils.invokeSx(props.labelSx, theme) as any,
                                ...erroneous && {color: "error.main",}
                            })}
                        >
                            {props.label ?? ""} {props.required && "*"}
                        </Typography>
                        {
                            !!props.onHistoryClicked &&
                            <Tooltip title={"Field's History"}>
                                <IconButton
                                    color={'paper.primary'}
                                    onClick={props.onHistoryClicked}
                                >
                                    {
                                        savingChanges
                                            ? <CircularProgress size={15} color={'primary'}/>
                                            : <HistoryRounded sx={{padding: 0.25}}/>
                                    }
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            valueChanged &&
                            <SaveOrRollback
                                sx={{marginInlineStart: 'auto'}}
                                loading={savingChanges}
                                onSave={saveChanges}
                                onRollback={rollbackChanges}
                                layout={'row'}
                                showText={false}
                            />
                        }
                    </Box>
                    {
                        <props.Field {...fieldProps}/>
                    }
                </Box>
            </FieldControlContext.Provider>
        </>
    );
}

export default FieldControl;
